import React, { useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import InviteSetupPage from './InviteSetupPage';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Package, ArrowLeft, Loader2, Mail, Eye, EyeOff, Building2, Store, Check, ArrowRight } from 'lucide-react';
import api, { setToken } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';

// ===== Google SVG Icon =====
function GoogleIcon() {
  return (
    <svg className="w-5 h-5" viewBox="0 0 24 24">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
    </svg>
  );
}

function MailRuIcon() {
  return (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="11" fill="#005FF9"/>
      <path d="M12 7C9.24 7 7 9.24 7 12C7 14.76 9.24 17 12 17C13.38 17 14.63 16.44 15.54 15.54L14.12 14.12C13.56 14.68 12.82 15 12 15C10.35 15 9 13.65 9 12C9 10.35 10.35 9 12 9C13.65 9 15 10.35 15 12V12.5C15 13.05 14.55 13.5 14 13.5C13.45 13.5 13 13.05 13 12.5V9.5H11V10.07C10.53 9.73 9.98 9.5 9.37 9.5C7.79 9.5 6.5 10.79 6.5 12.37C6.5 13.95 7.79 15.25 9.37 15.25C10.09 15.25 10.74 14.96 11.24 14.5C11.65 15 12.29 15.25 13 15.25C14.38 15.25 15.5 14.13 15.5 12.75V12C15.5 9.24 13.76 7 12 7Z" fill="white"/>
    </svg>
  );
}

// ===== ENTRY VIEW =====
function EntryView({ onGoogle, onEmail }) {
  return (
    <div className="space-y-3" data-testid="auth-entry-view">
      <Button
        onClick={onGoogle}
        variant="outline"
        className="w-full h-12 text-base font-medium gap-3 border-2 hover:bg-accent/80 transition-all"
        data-testid="continue-google-btn"
      >
        <GoogleIcon />
        Continue with Google
      </Button>

      <Button
        onClick={onEmail}
        variant="outline"
        className="w-full h-12 text-base font-medium gap-3 border-2 hover:bg-accent/80 transition-all"
        data-testid="continue-email-btn"
      >
        <Mail className="w-5 h-5" />
        Continue with Email
      </Button>

      <p className="text-center text-xs text-muted-foreground pt-2">
        By continuing, you agree to Pharm-X's terms of service.
      </p>
    </div>
  );
}

// ===== EMAIL FLOW =====
function EmailFlow({ onBack, defaultDomain = '' }) {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [step, setStep] = useState('email'); // email | login | register | forgot | forgot-sent
  const [email, setEmail] = useState('');
  const [emailSuffix] = useState(defaultDomain);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [name, setName] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checkResult, setCheckResult] = useState(null);
  const passwordRef = useRef(null);

  const checkEmail = async () => {
    if (!email.trim() || !email.includes('@')) {
      toast.error('Please enter a valid email');
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post('/auth/check-email', { email: email.trim().toLowerCase() });
      setCheckResult(data);
      if (data.exists) {
        if (data.has_password) {
          setStep('login');
        } else if (data.provider === 'google') {
          toast.info('This account uses Google sign-in. Please continue with Google.');
          onBack();
          return;
        } else {
          setStep('login');
        }
      } else {
        setStep('register');
      }
      setTimeout(() => passwordRef.current?.focus(), 100);
    } catch (e) {
      toast.error('Failed to check email');
    }
    setLoading(false);
  };

  const handleLogin = async () => {
    if (!password) { toast.error('Enter your password'); return; }
    setLoading(true);
    try {
      const { data } = await api.post('/auth/email/login', { email: email.trim().toLowerCase(), password });
      if (data.session_token) {
        setToken(data.session_token);
      }
      setUser(data);
      toast.success('Welcome back!');
      // Route based on role
      if (data.role === 'platform_owner' || data.role === 'platform_admin' || data.role === 'support_admin') {
        navigate('/internal/control-room');
      } else if (!data.active_workspace_id && data.role === 'pending') {
        navigate('/onboarding');
      } else if (data.role === 'mr') {
        window.location.href = '/mr-mobile';
        return;
      } else {
        const isPharmacy = ['apteka', 'pharmacy_owner', 'pharmacy_admin'].includes(data.role);
        navigate(isPharmacy ? '/pharmacy-dashboard' : '/dashboard');
      }
    } catch (e) {
      const msg = e.response?.data?.detail || 'Login failed';
      toast.error(msg);
    }
    setLoading(false);
  };

  const handleRegister = async () => {
    if (!name.trim()) { toast.error('Please enter your name'); return; }
    if (password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    if (password !== confirmPassword) { toast.error('Passwords do not match'); return; }
    setLoading(true);
    try {
      const { data } = await api.post('/auth/email/register', {
        email: email.trim().toLowerCase(), password, name: name.trim()
      });
      if (data.session_token) {
        setToken(data.session_token);
      }
      setUser(data);
      toast.success('Account created!');
      if (data.role === 'platform_owner') {
        navigate('/internal/control-room');
      } else {
        navigate('/onboarding');
      }
    } catch (e) {
      const msg = e.response?.data?.detail || 'Registration failed';
      toast.error(msg);
    }
    setLoading(false);
  };

  const handleForgotPassword = async () => {
    setLoading(true);
    try {
      await api.post('/auth/forgot-password', { email: email.trim().toLowerCase(), origin: window.location.origin });
      setStep('forgot-sent');
    } catch {
      toast.error('Failed to send reset link');
    }
    setLoading(false);
  };

  const handleKeyDown = (e, action) => {
    if (e.key === 'Enter') action();
  };

  return (
    <div className="space-y-4" data-testid="auth-email-flow">
      {/* Back button */}
      <button
        onClick={step === 'email' ? onBack : () => setStep('email')}
        className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
        data-testid="auth-back-btn"
      >
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      {/* Step: Email input */}
      {step === 'email' && (
        <div className="space-y-3">
          <h2 className="text-lg font-semibold">{emailSuffix ? 'Enter your Mail.ru email' : 'Enter your email'}</h2>
          <Input
            type="email"
            placeholder={emailSuffix ? `username${emailSuffix}` : 'you@example.com'}
            value={email}
            onChange={e => setEmail(e.target.value)}
            onKeyDown={e => handleKeyDown(e, checkEmail)}
            className="h-11"
            autoFocus
            data-testid="email-input"
          />
          <Button onClick={checkEmail} disabled={loading} className="w-full h-11" data-testid="email-continue-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Continue'}
          </Button>
        </div>
      )}

      {/* Step: Login */}
      {step === 'login' && (
        <div className="space-y-3">
          <h2 className="text-lg font-semibold">Welcome back</h2>
          <p className="text-sm text-muted-foreground">{email}</p>
          <div className="relative">
            <Input
              ref={passwordRef}
              type={showPw ? 'text' : 'password'}
              placeholder="Password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              onKeyDown={e => handleKeyDown(e, handleLogin)}
              className="h-11 pr-10"
              data-testid="password-input"
            />
            <button
              type="button"
              onClick={() => setShowPw(!showPw)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          <Button onClick={handleLogin} disabled={loading} className="w-full h-11" data-testid="login-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Login'}
          </Button>
          <button
            onClick={() => setStep('forgot')}
            className="text-xs text-primary hover:underline w-full text-center"
            data-testid="forgot-password-link"
          >
            Forgot password?
          </button>
        </div>
      )}

      {/* Step: Register */}
      {step === 'register' && (
        <div className="space-y-3">
          <h2 className="text-lg font-semibold">Create your account</h2>
          <p className="text-sm text-muted-foreground">{email}</p>
          <Input
            placeholder="Your name"
            value={name}
            onChange={e => setName(e.target.value)}
            className="h-11"
            autoFocus
            data-testid="name-input"
          />
          <div className="relative">
            <Input
              ref={passwordRef}
              type={showPw ? 'text' : 'password'}
              placeholder="Password (min 6 chars)"
              value={password}
              onChange={e => setPassword(e.target.value)}
              className="h-11 pr-10"
              data-testid="register-password-input"
            />
            <button
              type="button"
              onClick={() => setShowPw(!showPw)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          <Input
            type={showPw ? 'text' : 'password'}
            placeholder="Confirm password"
            value={confirmPassword}
            onChange={e => setConfirmPassword(e.target.value)}
            onKeyDown={e => handleKeyDown(e, handleRegister)}
            className="h-11"
            data-testid="confirm-password-input"
          />
          <Button onClick={handleRegister} disabled={loading} className="w-full h-11" data-testid="register-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create Account'}
          </Button>
        </div>
      )}

      {/* Step: Forgot password */}
      {step === 'forgot' && (
        <div className="space-y-3">
          <h2 className="text-lg font-semibold">Reset your password</h2>
          <p className="text-sm text-muted-foreground">
            We'll send a reset link to <span className="font-medium text-foreground">{email}</span>
          </p>
          <Button onClick={handleForgotPassword} disabled={loading} className="w-full h-11" data-testid="send-reset-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Send reset link'}
          </Button>
        </div>
      )}

      {/* Step: Forgot sent confirmation */}
      {step === 'forgot-sent' && (
        <div className="space-y-3 text-center">
          <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
            <Mail className="w-7 h-7 text-primary" />
          </div>
          <h2 className="text-lg font-semibold">Check your email</h2>
          <p className="text-sm text-muted-foreground">
            If an account exists for <span className="font-medium">{email}</span>, you'll receive a password reset link.
          </p>
          <button
            onClick={() => setStep('login')}
            className="text-xs text-primary hover:underline"
            data-testid="back-to-login-link"
          >
            Back to login
          </button>
        </div>
      )}
    </div>
  );
}

// ===== LANDING VIEW (NO TYPE SELECTED) =====
function LandingView({ onGoogle }) {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-[#f8fafb]" data-testid="landing-page">
      <header className="bg-white/80 backdrop-blur-sm border-b border-slate-100 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <img src="/logo-final-e.svg" alt="Pharm-X" className="h-9 w-auto object-contain" />
          </div>
          <button
            onClick={() => navigate('/login?type=signin')}
            className="text-sm font-medium text-slate-600 hover:text-[#0F1733] transition-colors px-4 py-2 rounded-lg hover:bg-slate-100"
            data-testid="header-signin-btn"
          >
            Sign In
          </button>
        </div>
      </header>
      <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-14 sm:pt-20 pb-10 text-center">
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-[#0F1733] tracking-tight leading-tight">
          Pharmacy Management<br /><span className="text-[#0F7B6C]">Made Simple</span>
        </h1>
        <p className="mt-5 text-base sm:text-lg text-slate-500 max-w-xl mx-auto leading-relaxed">
          Track stock, manage deliveries, control payments, and grow your pharmaceutical network.
        </p>
      </section>
      <section className="max-w-4xl mx-auto px-4 sm:px-6 pb-16">
        <h2 className="text-center text-base font-semibold text-slate-600 mb-8">Choose your account type to get started</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div
            onClick={() => navigate('/login?type=company')}
            className="group bg-white rounded-2xl border-2 border-slate-200 hover:border-blue-400 p-7 sm:p-8 cursor-pointer transition-all duration-200 hover:shadow-lg hover:shadow-blue-100/50 active:scale-[0.98]"
            data-testid="landing-company-card"
          >
            <div className="w-14 h-14 rounded-2xl bg-blue-50 flex items-center justify-center mb-4 group-hover:bg-blue-500 transition-colors">
              <Building2 className="w-7 h-7 text-blue-500 group-hover:text-white transition-colors" />
            </div>
            <h3 className="text-lg font-bold text-[#0F1733] mb-1.5">Pharma Company</h3>
            <p className="text-sm text-slate-500 mb-4">Distribute medicines, manage pharmacies, and track your sales network.</p>
            <ul className="space-y-1.5 mb-5 text-left">
              {['Manage partner pharmacies', 'Track deliveries & payments', 'Field team (MR) management', 'Analytics & intelligence'].map(t => (
                <li key={t} className="flex items-center gap-2 text-sm text-slate-600"><Check className="w-3.5 h-3.5 text-blue-500 shrink-0" />{t}</li>
              ))}
            </ul>
            <div className="flex items-center gap-2 text-blue-600 font-semibold text-sm group-hover:gap-3 transition-all">
              Get Started <ArrowRight className="w-4 h-4" />
            </div>
          </div>
          <div
            onClick={() => navigate('/login?type=pharmacy')}
            className="group bg-white rounded-2xl border-2 border-slate-200 hover:border-emerald-400 p-7 sm:p-8 cursor-pointer transition-all duration-200 hover:shadow-lg hover:shadow-emerald-100/50 active:scale-[0.98]"
            data-testid="landing-pharmacy-card"
          >
            <div className="w-14 h-14 rounded-2xl bg-emerald-50 flex items-center justify-center mb-4 group-hover:bg-emerald-500 transition-colors">
              <Store className="w-7 h-7 text-emerald-500 group-hover:text-white transition-colors" />
            </div>
            <h3 className="text-lg font-bold text-[#0F1733] mb-1.5">Pharmacy</h3>
            <p className="text-sm text-slate-500 mb-4">Track your stock, manage payments, and connect with suppliers.</p>
            <ul className="space-y-1.5 mb-5 text-left">
              {['Track stock levels', 'View payables & payments', 'Reorder suggestions', 'Connect with suppliers'].map(t => (
                <li key={t} className="flex items-center gap-2 text-sm text-slate-600"><Check className="w-3.5 h-3.5 text-emerald-500 shrink-0" />{t}</li>
              ))}
            </ul>
            <div className="flex items-center gap-2 text-emerald-600 font-semibold text-sm group-hover:gap-3 transition-all">
              Get Started <ArrowRight className="w-4 h-4" />
            </div>
          </div>
        </div>
      </section>
      <footer className="py-6 text-center text-xs text-slate-400">Pharm-X &copy; {new Date().getFullYear()}</footer>
    </div>
  );
}

// ===== MAIN LOGIN PAGE =====
export default function LoginPage() {
  const [view, setView] = useState('entry'); // entry | email
  const [searchParams] = useSearchParams();
  const accountType = searchParams.get('type'); // 'company' | 'pharmacy' | null
  const inviteToken = searchParams.get('invite');

  // Show Google OAuth errors from redirect
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const error = params.get('error');
    if (error) {
      const messages = {
        google_denied: 'Google login was cancelled.',
        no_code: 'Google login failed — no authorization code received.',
        token_failed: 'Google login failed — could not verify with Google.',
        profile_failed: 'Google login failed — could not fetch your profile.',
        config_error: 'Google login is not configured on this server.',
        server_error: 'Something went wrong. Please try again.',
      };
      toast.error(messages[error] || 'Login failed. Please try again.');
      // Clean URL
      window.history.replaceState(null, '', '/login');
    }
  }, []);

  // Google OAuth: Use Emergent-managed auth on preview domains,
  // native Google OAuth on production/custom domains.
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleGoogle = async () => {
    // Store the account type in sessionStorage so onboarding can read it
    if (accountType) {
      sessionStorage.setItem('pharmx_signup_type', accountType);
    }
    const isEmergentDomain = window.location.hostname.endsWith('.emergentagent.com') || 
                             window.location.hostname.endsWith('.emergent.host');
    if (isEmergentDomain) {
      // Emergent-managed OAuth for preview
      const redirectUrl = window.location.origin + '/auth/callback';
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    } else {
      // Native Google OAuth for production/custom domains
      try {
        const res = await api.get('/auth/google/init');
        window.location.href = res.data.url;
      } catch (err) {
        const detail = err?.response?.data?.detail;
        const why = !err?.response ? 'server not reachable' : typeof detail === 'string' ? detail : `server error ${err.response.status}`;
        toast.error(`Google login is not available (${why}). Please use email login.`, { duration: 9000 });
      }
    }
  };

  const typeLabel = accountType === 'company' ? 'Pharma Company' : accountType === 'pharmacy' ? 'Pharmacy' : accountType === 'signin' ? 'Pharm-X' : null;
  const TypeIcon = accountType === 'company' ? Building2 : accountType === 'pharmacy' ? Store : Package;
  const typeBg = accountType === 'company' ? 'bg-blue-500' : accountType === 'pharmacy' ? 'bg-emerald-500' : 'bg-[#0F7B6C]';

  // Show landing page when no type is selected
  if (!accountType && !inviteToken) {
    return <LandingView onGoogle={handleGoogle} />;
  }

  // If invite token present, render InviteSetupPage inline
  if (inviteToken) {
    return <InviteSetupPage inviteToken={inviteToken} />;
  }

  const headingText = view === 'entry'
    ? (accountType === 'signin' ? 'Sign in to Pharm-X' : typeLabel ? `${typeLabel} Account` : 'Welcome back')
    : 'Continue with Email';
  const subtitleText = view === 'entry'
    ? (accountType === 'signin' ? 'Choose how you want to sign in' : 'Sign in or create an account')
    : 'Enter your details below';

  return (
    <div className="min-h-screen flex bg-background" data-testid="login-page">
      {/* Left panel — branding */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1765031092161-a9ebe556117e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwxfHxwaGFybWFjeSUyMG1lZGljaW5lJTIwc2hlbHZlc3xlbnwwfHx8fDE3NzIzNTQwMTV8MA&ixlib=rb-4.1.0&q=85"
          alt="Pharmacy"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-primary/80" />
        <div className="relative z-10 flex flex-col justify-end p-12 text-white">
          <h2 className="text-4xl font-bold tracking-tight mb-4 font-manrope"><img src="/logo-final-e.svg" alt="Pharm-X" className="h-14 w-auto object-contain" /></h2>
          <p className="text-lg opacity-90 max-w-md leading-relaxed">
            {accountType === 'company'
              ? 'Manage your pharmacy network, track deliveries, and control payments — all in one place.'
              : accountType === 'pharmacy'
              ? 'Track your stock, manage suppliers, and stay on top of your payments effortlessly.'
              : 'Complete pharmacy management platform. Track stock, manage deliveries, control finances, and grow your network.'}
          </p>
        </div>
      </div>

      {/* Right panel — auth */}
      <div className="flex-1 flex items-start pt-16 sm:items-center sm:pt-0 justify-center p-6 sm:p-8">
        <div className="w-full max-w-sm space-y-8">
          {/* Logo + heading */}
          <div className="flex flex-col items-center gap-4">
            <img src="/logo-final-e.svg" alt="Pharm-X" className="w-14 h-14 object-contain" />
            <div className="text-center">
              <h1 className="text-2xl font-bold tracking-tight font-manrope">
                {headingText}
              </h1>
              <p className="text-muted-foreground text-sm mt-1">
                {subtitleText}
              </p>
            </div>
          </div>

          {/* Auth views */}
          {view === 'entry' && (
            <EntryView
              onGoogle={handleGoogle}
              onEmail={() => {
                if (accountType) {
                  sessionStorage.setItem('pharmx_signup_type', accountType);
                }
                setView('email');
              }}
            />
          )}

          {view === 'email' && (
            <EmailFlow onBack={() => setView('entry')} />
          )}

          <p className="text-center text-xs text-muted-foreground/70" data-testid="browser-hint">
            Having trouble? Try opening this page in Safari, Chrome, or another full browser.
          </p>
        </div>
      </div>
    </div>
  );
}
